import {
  TEAM_OVERVIEW_BOARD,
  TEAM_OVERVIEW_LAYOUT_VERSION,
  type TeamOverviewWidgetKey,
} from "@salestrak/widgets";
import { AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { type ManagedWidget, withManagedLayoutVersion } from "./managed-widgets";

export { TEAM_OVERVIEW_LAYOUT_VERSION };

/**
 * Display copy for each Team Overview board entry — the board DATA lives in
 * @salestrak/widgets (shared with the core API's first-read
 * seeding). The `TeamOverviewWidgetKey` union makes a new board entry a
 * typecheck error until it gets copy.
 */
const PRESENTATION: Record<
  TeamOverviewWidgetKey,
  Pick<ManagedWidget, "label" | "description" | "color">
> = {
  "team-overview.unassigned": {
    label: "Unassigned",
    description: "The team's Projects with no assignee, leaving out Done and Canceled.",
  },
  "team-overview.workload": {
    label: "Workload",
    description: "The team's Projects per assignee, busiest first, leaving out Done and Canceled.",
  },
  "team-overview.follow-ups": {
    label: "Follow ups",
    description: `The team's Project follow ups — banded ${AGE_STATUS_CONFIG.offTrack.label}, ${AGE_STATUS_CONFIG.atRisk.label} and ${AGE_STATUS_CONFIG.onTrack.label}, the nearest deadline first.`,
    color: "orange",
  },
  "team-overview.activity": {
    label: "Recent activity",
    description: "The latest activity on the team's Projects.",
  },
  "team-overview.outcomes-v2": {
    label: "Update outcomes",
    description: "How Updates on the team's Projects ended, per member.",
    color: "green",
  },
};

/**
 * The Team Overview managed widget catalog — the fixed, curated set the Team
 * Overview tab offers. Team Overview is NOT a free-form add-any-widget surface:
 * the user's only control is which of THESE widgets are visible (toggle on/off
 * via the Manage-widgets panel). Users can arrange the widgets and switch each
 * one between Half width and Full width.
 *
 * Because Team Overview is scoped by the ambient team `IssueScope`, every widget
 * is already narrowed to that team. This catalog persists on its own view via
 * `surfaceVariant="overview"`, using the same managed model as My Work.
 */
export const TEAM_OVERVIEW_WIDGETS: ManagedWidget[] = TEAM_OVERVIEW_BOARD.map((entry) => ({
  ...entry,
  config: withManagedLayoutVersion(TEAM_OVERVIEW_LAYOUT_VERSION, entry.config),
  ...PRESENTATION[entry.widgetKey],
}));
